/**
 * User Context - Optimised with Microservices
 *
 * Uses server-fetched microservices data for optimal performance.
 * This context is designed to work with Server Components that pass
 * microservices data as props, avoiding unnecessary client-side fetches.
 */

'use client';

import { getMembershipTaxonomyName } from '@/constants/membership';
import type {
  ContactActiveGroups,
  ContactClaimStatus,
  ContactDetails,
  ContactIdentity,
  ContactInterests,
  ContactMembership,
  ContactRequestedGroups,
  ContactStatus,
} from '@/types/microservices';
import { createContext, useContext, type ReactNode } from 'react';

/**
 * Convert Drupal relative URL to absolute URL
 */
function toAbsoluteUrl(pathOrUrl?: string | null): string {
  if (!pathOrUrl) return '';
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const base = process.env.NEXT_PUBLIC_DRUPAL_BASE_URL || process.env.DRUPAL_URL || '';
  if (!base) return pathOrUrl;
  const cleanBase = base.replace(/\/$/, '');
  const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return `${cleanBase}${path}`;
}

interface UserContextValue {
  // Identity data
  name: string | null;
  email: string | null;
  firstName: string | null;
  familyName: string | null;
  contactId: string | null;

  // Company data
  companyName: string | null;
  companyCode: string | null;
  companyOther: string | null;
  companyAcronym: string | null;

  // Details data
  civility: string | null;
  positionTitle: string | null;
  departmentService: string | null;
  workCountry: string | null;
  telephone: string | null;
  mobilePhone: string | null;
  emailAlternatif: string | null;
  pictureUrl: string | null;

  // Membership data (membership label used for permissions)
  membership: string | null;
  membershipTaxonomyName: string | null;

  // Claim status
  claimed: boolean;

  // Groups data
  activeGroups: string[];
  requestedGroups: string[];

  // Applications data
  activeApplications: string[];

  // Interests data
  interests: string[];
  interestsIds: string[];

  // Loading states
  isLoading: boolean;

  // Raw microservices data (for advanced usage)
  _raw?: {
    identity: ContactIdentity | null;
    details: ContactDetails | null;
    membership: ContactMembership | null;
    activeGroups: ContactActiveGroups | null;
    requestedGroups: ContactRequestedGroups | null;
    interests: ContactInterests | null;
    status: ContactStatus | null;
    // Keep claimStatus for backward compatibility
    claimStatus: ContactClaimStatus | null;
  };
}

const UserContext = createContext<UserContextValue | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
  /** Identity data from server-side microservices */
  identity: ContactIdentity | null;
  /** Details data from server-side microservices */
  details: ContactDetails | null;
  /** Membership data from server-side microservices */
  membership: ContactMembership | null;
  /** Active groups data from server-side microservices */
  activeGroups: ContactActiveGroups | null;
  /** Requested groups data from server-side microservices */
  requestedGroups: ContactRequestedGroups | null;
  /** Interests data from server-side microservices */
  interests: ContactInterests | null;
  /** Contact status data from server-side microservices */
  status: ContactStatus | null;
  /** Claim status data from server-side microservices (deprecated, use status) */
  claimStatus: ContactClaimStatus | null;
}

/**
 * User Context Provider
 *
 * Provides user data from server-fetched microservices.
 * Should be used in layouts or pages that fetch microservices server-side.
 *
 * @example
 * ```tsx
 * // In a Server Component
 * export default async function Layout({ children }) {
 *   const email = await getUserEmail();
 *   const data = await getAllContactData(email);
 *
 *   return (
 *     <UserProvider
 *       identity={data.identity}
 *       details={data.details}
 *       membership={data.membership}
 *       activeGroups={data.activeGroups}
 *       requestedGroups={data.requestedGroups}
 *       interests={data.interests}
 *     >
 *       {children}
 *     </UserProvider>
 *   );
 * }
 * ```
 */
export function UserProvider({
  children,
  identity,
  details,
  membership,
  activeGroups: activeGroupsData,
  requestedGroups: requestedGroupsData,
  interests: interestsData,
  status,
  claimStatus,
}: UserProviderProps) {
  // Extract arrays from microservices data (already parsed)
  const activeGroups = activeGroupsData?.activeGroups || [];
  const requestedGroups = requestedGroupsData?.requestedGroups || [];
  const interestsList = interestsData?.interests || [];
  const interestsIdsList = interestsData?.interestsIds || [];
  // Extract activeApplications from details (returned by /api/v1/contact/details endpoint)
  const activeApplications = details?.activeApplications || [];

  // Get company code
  const companyCode = identity?.companyCode || membership?.companyCode || null;

  /**
   * Business Rule: UIC Association Members Exception
   *
   * Users with companyCode === '-1' are UIC association members (internal staff).
   * They should always have "Active member" status regardless of microservice data.
   * This ensures internal staff have full access to all member features.
   */
  const isUicOrg = companyCode === '-1';
  const effectiveMembership = isUicOrg ? 'Active member' : membership?.membershipLabel || null;

  // Get taxonomy name for membership (for Drupal API queries)
  const membershipTaxonomyName = getMembershipTaxonomyName(effectiveMembership);

  // Get claim status - prefer new status endpoint, fallback to claimStatus for backward compatibility
  const claimed = status?.status === 'active' || claimStatus?.status === 'claimed';

  // Get contact ID from identity (use uuid instead of nid for Drupal API)
  const contactId = identity?.uuid || null;

  const value: UserContextValue = {
    // Identity
    name: identity?.name || null,
    email: identity?.email || null,
    firstName: identity?.firstName || null,
    familyName: identity?.familyName || null,
    contactId,

    // Company
    companyName: identity?.companyName || membership?.companyName || null,
    companyCode,
    companyOther: identity?.companyOther || membership?.companyOther || null,
    companyAcronym: details?.companyAcronym || null,

    // Details
    civility: details?.civility || null,
    positionTitle: details?.positionTitle || null,
    departmentService: details?.departmentService || null,
    workCountry: details?.workCountry || null,
    telephone: details?.telephone || null,
    mobilePhone: details?.mobilePhone || null,
    emailAlternatif: details?.emailAlternatif || null,
    // Convert relative Drupal URL to absolute URL
    pictureUrl: toAbsoluteUrl(details?.pictureUrl) || null,

    // Membership (from membershipLabel with -1 exception)
    membership: effectiveMembership,
    membershipTaxonomyName,

    // Claim status
    claimed,

    // Groups
    activeGroups,
    requestedGroups,

    // Applications
    activeApplications,

    // Interests
    interests: interestsList,
    interestsIds: interestsIdsList,

    // Loading (always false since data is already loaded)
    isLoading: false,

    // Raw data
    _raw: {
      identity,
      details,
      membership,
      activeGroups: activeGroupsData,
      requestedGroups: requestedGroupsData,
      interests: interestsData,
      status,
      claimStatus,
    },
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

/**
 * Hook to access user data from microservices
 *
 * @throws Error if used outside UserProvider
 *
 * @example
 * ```tsx
 * function UserProfile() {
 *   const { name, email, pictureUrl } = useUser();
 *   return <div>{name} - {email}</div>;
 * }
 * ```
 */
export function useUser(): UserContextValue {
  const context = useContext(UserContext);

  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }

  return context;
}

